<script>
import baseMixin from '@/mixins/baseMixin'
import { mapGetters, mapActions } from 'vuex'
// import { isNullOrEmptyArray } from '@/helpers/array-helpers'

import CommentType from '@/constants/comments/CommentType'
import ClientDetailsListItem from '../general/client-details-list-item.vue'
import DisputesTableBookingDateV2 from './disputes-table-booking-date-v2.vue'
import DisputesTableSubmittedIndicator from './disputes-table-submitted-indicator.vue'
import DisputesTableBreaks from './disputes-table-breaks.vue'
import DisputesTableRestBreaksIndicator from './disputes-table-rest-breaks-indicator.vue'

export default {
  components: {
    ClientDetailsListItem,
    DisputesTableBookingDateV2,
    DisputesTableSubmittedIndicator,
    DisputesTableBreaks,
    DisputesTableRestBreaksIndicator,
  },
  mixins: [baseMixin],
  props: {
    autocompleteSelectedModel: {
      type: Object,
      default: () => null,
    },
  },
  computed: {
    ...mapGetters({
      getUserNameById: 'users/getUserNameById',
      disputeTasks: 'disputes/disputeFollowUpTasks',
      isLoadingDisputeTasks: 'disputes/isLoadingDisputeTasks',
    }),
    showRestBreaksColumn() {
      return Boolean(
        this.disputeTasks.find(
          (disputeTask) => disputeTask?.isRestBreakCompliant !== null
        )
      )
    },
    filters() {
      return {
        filter: this.autocompleteSelectedModel,
      }
    },
    baseTableHeaders() {
      return [
        {
          text: 'Booking Id',
          align: 'start',
          value: 'bookingId',
        },
        { text: 'Location Details', value: 'locationName' },
        { text: 'Candidate', value: 'candidateName' },
        { text: 'Booking Time', value: 'bookingDateTime' },
        { text: 'Breaks', value: 'breakMinutes' },
        { text: 'Task Type', value: 'taskType' },
        {
          text: 'Rest Breaks',
          value: 'isRestBreakCompliant',
          display: this.showRestBreaksColumn,
          align: 'center',
        },
        {
          text: 'Ca',
          value: 'candidateSubmittedDate',
          align: 'center',
        },
        {
          text: 'Cl',
          value: 'clientSubmittedDate',
          align: 'center',
        },
        { text: 'Assigned To', value: 'assignedTo' },
        { text: '', value: 'actions', align: 'end', sortable: false },
      ]
    },
    tableHeaders() {
      return this.baseTableHeaders.filter(
        (header) => !Object.hasOwn(header, 'display') || header?.display
      )
    },
  },
  methods: {
    ...mapActions({
      getDisputeFollowUpTasks: 'disputes/getDisputeFollowUpTasks',
    }),
    taskDescriptionFromType(taskType) {
      switch (taskType) {
        case CommentType.DisputeFollowUpNoTimesheetsSubmitted:
          return { text: 'No timesheets submitted', color: 'warning' }
        case CommentType.DisputeFollowUpCandidateModified:
          return { text: 'Candidate modified timesheet', color: 'blue' }
        case CommentType.DisputeFollowUpCandidateStatedDidNotHappen:
          return { text: "Candidate stated didn't happen", color: 'red' }
        case CommentType.DisputeFollowUpClientModified:
          return { text: 'Client modified timesheet', color: 'blue' }
        case CommentType.DisputeFollowUpClientStatedDidNotHappen:
          return { text: "Client stated didn't happen", color: 'red' }
        case CommentType.DisputeFollowUpTimesheetConflict:
          return { text: 'Timesheet conflict', color: 'orange' }
        case CommentType.DisputeFollowUpCandidateTimesheetNotSubmitted:
          return { text: 'Candidate did not submit timesheet', color: 'purple' }
        default:
          return { text: 'Unknown', color: 'grey' }
      }
    },
    hasRestBreakCompliantValue(item) {
      return (
        item?.isRestBreakCompliant !== null &&
        typeof item?.isRestBreakCompliant !== 'undefined'
      )
    },
    hasSubmittedTimesheet(submittedDate) {
      return Boolean(submittedDate)
    },
    mockedSubmittedTimesheetObject(date) {
      return {
        submittedDateUtc: date,
      }
    },
    /**
     * The table expects a function to return the item class.
     */
    getTableItemClass() {
      return 'cursor-pointer'
    },
    openTaskResolveForm(item) {
      this.$emit('openTask', {
        bookingId: item.bookingId,
        taskId: item.taskId,
      })
    },
    getAssigneeName(adminId) {
      return this.getUserNameById(adminId)
    },
  },
}
</script>

<template>
  <div>
    <v-data-table
      :headers="tableHeaders"
      :items="disputeTasks"
      :loading="isLoadingDisputeTasks"
      :class="['rounded-xl custom-elevation pt-2 overflow-y-auto']"
      :item-class="getTableItemClass"
      @click:row="openTaskResolveForm"
    >
      <template v-slot:item.bookingId="{ item }">
        <div>
          <BaseLink
            :href="returnBookingUrl(item.bookingId)"
            color="primary"
            :title="$t('general.link.viewBookingTooltip')"
            allow-insecure
            >{{ item.bookingId }}</BaseLink
          >
        </div>
      </template>
      <template v-slot:item.locationName="{ item }">
        <ClientDetailsListItem :location-id="item.locationId" />
      </template>
      <template v-slot:item.candidateName="{ item }">
        <div>
          <BaseLink
            :href="returnCandidateUrl(item.candidateId)"
            color="primary"
            title="View candidate in relief roster"
            allow-insecure
            >{{ item.candidateName }}</BaseLink
          >
        </div>
      </template>

      <template v-slot:item.breakMinutes="{ item }">
        <DisputesTableBreaks :dispute="item" />
      </template>

      <template v-slot:item.taskType="{ item }">
        <v-chip small :color="isDarkMode ? '' : 'grey lighten-4'">
          <v-badge
            inline
            left
            dot
            :color="taskDescriptionFromType(item.taskType).color"
            class="ml-n1"
            offset-x="2px"
            ><div class="ml-1">{{
              taskDescriptionFromType(item.taskType).text
            }}</div></v-badge
          >
        </v-chip>
      </template>

      <template v-slot:item.bookingDateTime="{ item }">
        <DisputesTableBookingDateV2 :dispute="item" />
      </template>

      <template v-slot:item.candidateSubmittedDate="{ item }">
        <div class="d-flex align-center justify-center">
          <DisputesTableSubmittedIndicator
            v-if="hasSubmittedTimesheet(item.candidateSubmittedDate)"
            class="w-fit-content"
            :timesheet="
              mockedSubmittedTimesheetObject(item.candidateSubmittedDate)
            "
          />
        </div>
      </template>
      <template v-slot:item.clientSubmittedDate="{ item }">
        <div class="d-flex align-center justify-center">
          <DisputesTableSubmittedIndicator
            v-if="hasSubmittedTimesheet(item.clientSubmittedDate)"
            class="w-fit-content"
            :timesheet="
              mockedSubmittedTimesheetObject(item.clientSubmittedDate)
            "
          />
        </div>
      </template>

      <template v-slot:item.isRestBreakCompliant="{ item }">
        <div class="d-flex align-center justify-center">
          <DisputesTableRestBreaksIndicator
            v-if="hasRestBreakCompliantValue(item)"
            class="w-fit-content"
            :compliant="item.isRestBreakCompliant"
          />
        </div>
      </template>

      <template v-slot:item.assignedTo="{ item }">
        {{ getAssigneeName(item.assignedTo) }}
      </template>

      <template v-slot:item.actions>
        <v-icon class="opacity-6">mdi-chevron-right</v-icon>
      </template>
    </v-data-table>
  </div>
</template>
